import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ClipboardPaste,
  Copy,
  Download,
  Eye,
  FolderPlus,
  FolderUp,
  History,
  Info,
  LayoutGrid,
  Link2,
  List,
  PenLine,
  Scissors,
  Search,
  ShieldCheck,
  Trash2,
  Upload,
  UploadCloud,
  X,
} from 'lucide-react';
import type { Nodo } from '@doc/shared';
import { tipoDeVista } from '@doc/shared';
import { useSesion } from '../../lib/sesion.js';
import { tamano as tamanoTexto } from '../../lib/formato.js';
import { subir, urlArchivo } from '../../lib/api.js';
import { useAvisos } from '../../ui/avisos.js';
import { Confirmar } from '../../ui/Modal.js';
import { MenuContextual, type OpcionMenu } from '../../ui/MenuContextual.js';
import { ArbolCarpetas } from './ArbolCarpetas.js';
import { ListaNodos, ordenar, type CampoOrden, type Orden } from './ListaNodos.js';
import { PanelDetalles } from './PanelDetalles.js';
import { DialogoNombre } from './DialogoNombre.js';
import { DialogoPermisos } from './DialogoPermisos.js';
import { DialogoVersiones } from './DialogoVersiones.js';
import { DialogoCompartir } from './DialogoCompartir.js';
import { VisorDocumento } from './VisorDocumento.js';
import { useAcciones, useArbol, useBusqueda, useCarpeta, useCuotaDeNodo } from './consultas.js';
import { MAXIMO_ARCHIVOS, entradasDeArrastre, entradasDeSelector, type EntradaSubida } from './carpetas.js';

type Vista = 'lista' | 'cuadricula';
interface Portapapeles {
  ids: string[];
  modo: 'copiar' | 'cortar';
}
interface Subida {
  id: number;
  nombre: string;
  porcentaje: number;
  error?: string;
}

/** Lo que el visor sabe mostrar; el resto solo se puede descargar. */
const previsualizable = (nodo: Nodo) => tipoDeVista(nodo.mime, nodo.nombre) !== 'NINGUNO';

export function ExploradorPage() {
  const { carpetaId } = useParams();
  const navegar = useNavigate();
  const { usuario } = useSesion();
  const { avisar, avisarError } = useAvisos();
  const acciones = useAcciones();

  const actual = carpetaId ?? usuario?.raizId ?? null;

  const { data: arbol } = useArbol();
  const { data: cuota } = useCuotaDeNodo(actual);
  const { data: listado, isLoading, isError, error, refetch } = useCarpeta(actual);

  const [busqueda, setBusqueda] = useState('');
  const { data: resultados } = useBusqueda(busqueda, actual ?? undefined);
  const buscando = busqueda.trim().length >= 2;

  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const [ancla, setAncla] = useState<number | null>(null);
  const [portapapeles, setPortapapeles] = useState<Portapapeles | null>(null);
  const [vista, setVista] = useState<Vista>(() => (localStorage.getItem('gestor-doc-vista') as Vista) ?? 'lista');
  const [orden, setOrden] = useState<Orden>({ campo: 'nombre', ascendente: true });
  const [verDetalles, setVerDetalles] = useState(true);
  const [menu, setMenu] = useState<{ x: number; y: number; nodo: Nodo | null } | null>(null);
  const [dialogo, setDialogo] = useState<
    'carpeta' | 'renombrar' | 'permisos' | 'versiones' | 'eliminar' | 'compartir' | null
  >(null);
  const [visorId, setVisorId] = useState<string | null>(null);
  const [arrastrandoArchivos, setArrastrandoArchivos] = useState(false);
  const [subidas, setSubidas] = useState<Subida[]>([]);
  const entradaArchivos = useRef<HTMLInputElement>(null);
  const entradaCarpeta = useRef<HTMLInputElement>(null);
  const contadorArrastre = useRef(0);

  const carpeta = listado?.carpeta ?? null;
  const nodos = useMemo(
    () => ordenar(buscando ? (resultados ?? []) : (listado?.hijos ?? []), orden),
    [buscando, resultados, listado, orden],
  );
  const seleccionados = useMemo(() => nodos.filter((n) => seleccion.has(n.id)), [nodos, seleccion]);
  const enVisor = useMemo(() => nodos.find((n) => n.id === visorId) ?? null, [nodos, visorId]);
  const unico = seleccionados.length === 1 ? seleccionados[0]! : null;

  useEffect(() => localStorage.setItem('gestor-doc-vista', vista), [vista]);
  // Al cambiar de carpeta la selección anterior deja de tener sentido.
  useEffect(() => {
    setSeleccion(new Set());
    setAncla(null);
  }, [actual, busqueda]);

  const irA = useCallback(
    (id: string) => {
      setBusqueda('');
      navegar(`/archivos/${id}`);
    },
    [navegar],
  );

  // ── Selección ─────────────────────────────────────────────────────────
  const alClicear = (evento: MouseEvent, nodo: Nodo, indice: number) => {
    evento.stopPropagation();
    if (evento.shiftKey && ancla !== null) {
      const [desde, hasta] = ancla < indice ? [ancla, indice] : [indice, ancla];
      setSeleccion(new Set(nodos.slice(desde, hasta + 1).map((n) => n.id)));
      return;
    }
    if (evento.metaKey || evento.ctrlKey) {
      setSeleccion((previa) => {
        const siguiente = new Set(previa);
        if (siguiente.has(nodo.id)) siguiente.delete(nodo.id);
        else siguiente.add(nodo.id);
        return siguiente;
      });
      setAncla(indice);
      return;
    }
    setSeleccion(new Set([nodo.id]));
    setAncla(indice);
  };

  const descargar = useCallback((nodo: Nodo) => {
    const enlace = document.createElement('a');
    enlace.href = urlArchivo(nodo.id, 'descargar');
    enlace.rel = 'noreferrer';
    document.body.appendChild(enlace);
    enlace.click();
    enlace.remove();
  }, []);

  const abrir = useCallback(
    (nodo: Nodo) => {
      if (nodo.tipo === 'CARPETA') {
        irA(nodo.id);
        return;
      }
      // Abrir un documento es verlo, no bajarlo: solo cuando no hay forma de
      // mostrarlo se cae a la descarga.
      if (previsualizable(nodo)) setVisorId(nodo.id);
      else descargar(nodo);
    },
    [irA, descargar],
  );

  // ── Mutaciones ────────────────────────────────────────────────────────
  const mover = useCallback(
    async (ids: string[], destinoId: string) => {
      try {
        await acciones.mover.mutateAsync({ ids, destinoId });
        avisar(ids.length === 1 ? 'Elemento movido' : `${ids.length} elementos movidos`);
        setSeleccion(new Set());
      } catch (e) {
        avisarError(e);
      }
    },
    [acciones.mover, avisar, avisarError],
  );

  const pegar = useCallback(async () => {
    if (!portapapeles || !carpeta) return;
    try {
      if (portapapeles.modo === 'copiar') {
        await acciones.copiar.mutateAsync({ ids: portapapeles.ids, destinoId: carpeta.id });
        avisar('Elementos copiados');
      } else {
        await acciones.mover.mutateAsync({ ids: portapapeles.ids, destinoId: carpeta.id });
        avisar('Elementos movidos');
        setPortapapeles(null);
      }
    } catch (e) {
      avisarError(e);
    }
  }, [portapapeles, carpeta, acciones, avisar, avisarError]);

  const eliminar = useCallback(async () => {
    const ids = seleccionados.filter((n) => n.permisos.eliminar).map((n) => n.id);
    if (ids.length === 0) return;
    try {
      await acciones.eliminar.mutateAsync(ids);
      avisar(ids.length === 1 ? 'Enviado a la papelera' : `${ids.length} elementos en la papelera`);
      setSeleccion(new Set());
    } catch (e) {
      avisarError(e);
    }
  }, [seleccionados, acciones, avisar, avisarError]);

  // ── Subidas ───────────────────────────────────────────────────────────
  const subirArchivos = useCallback(
    (entradas: EntradaSubida[]) => {
      if (!carpeta?.permisos.escribir) {
        avisar('No tienes permiso de escritura en esta carpeta', 'aviso');
        return;
      }
      if (entradas.length === 0) {
        avisar('No hay archivos que subir: la carpeta está vacía', 'aviso');
        return;
      }
      if (entradas.length > MAXIMO_ARCHIVOS) {
        avisar(`Son ${entradas.length} archivos y el máximo por subida son ${MAXIMO_ARCHIVOS}. Divídelos en tandas.`, 'aviso');
        return;
      }

      const id = Date.now();
      const raiz = entradas.find((e) => e.ruta)?.ruta.split('/')[0];
      const nombre = raiz
        ? `${raiz} · ${entradas.length} archivos`
        : entradas.length === 1
          ? entradas[0]!.archivo.name
          : `${entradas.length} archivos`;
      setSubidas((previas) => [...previas, { id, nombre, porcentaje: 0 }]);

      const formulario = new FormData();
      // Los campos van antes que los archivos: así el servidor ya tiene las
      // rutas cuando empieza a procesar el contenido.
      for (const entrada of entradas) formulario.append('rutas', entrada.ruta);
      for (const entrada of entradas) formulario.append('archivos', entrada.archivo);
      // Subir un archivo con un nombre que ya existe publica una versión nueva
      // en lugar de pisar el anterior; es la razón de ser del gestor.
      formulario.append('conflicto', 'VERSION');

      const { promesa } = subir<Nodo[]>(`/nodos/${carpeta.id}/subir`, formulario, (porcentaje) =>
        setSubidas((previas) => previas.map((s) => (s.id === id ? { ...s, porcentaje } : s))),
      );

      void promesa
        .then((subidos) => {
          void refetch();
          avisar(
            subidos.length === 1
              ? `"${subidos[0]!.nombre}" subido`
              : `${subidos.length} archivos subidos${raiz ? ` en «${raiz}»` : ''}`,
          );
          setSubidas((previas) => previas.filter((s) => s.id !== id));
        })
        .catch((e) => {
          avisarError(e);
          setSubidas((previas) =>
            previas.map((s) => (s.id === id ? { ...s, error: e instanceof Error ? e.message : 'Falló' } : s)),
          );
          setTimeout(() => setSubidas((previas) => previas.filter((s) => s.id !== id)), 6000);
        });
    },
    [carpeta, avisar, avisarError, refetch],
  );

  // ── Atajos de teclado ─────────────────────────────────────────────────
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      const elemento = document.activeElement;
      const escribiendo = elemento instanceof HTMLInputElement || elemento instanceof HTMLTextAreaElement;
      if (escribiendo || dialogo || menu || visorId) return;

      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        setSeleccion(new Set(nodos.map((n) => n.id)));
      } else if (meta && e.key.toLowerCase() === 'c' && seleccionados.length > 0) {
        setPortapapeles({ ids: seleccionados.map((n) => n.id), modo: 'copiar' });
        avisar(`${seleccionados.length} elemento(s) copiados`, 'info');
      } else if (meta && e.key.toLowerCase() === 'x' && seleccionados.every((n) => n.permisos.eliminar)) {
        if (seleccionados.length === 0) return;
        setPortapapeles({ ids: seleccionados.map((n) => n.id), modo: 'cortar' });
        avisar(`${seleccionados.length} elemento(s) listos para mover`, 'info');
      } else if (meta && e.key.toLowerCase() === 'v' && portapapeles) {
        void pegar();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (seleccionados.length > 0 && seleccionados.every((n) => n.permisos.eliminar)) {
          e.preventDefault();
          setDialogo('eliminar');
        }
      } else if (e.key === 'F2' && unico?.permisos.escribir) {
        setDialogo('renombrar');
      } else if (e.key === 'Enter' && unico) {
        abrir(unico);
      } else if (e.key === 'Escape') {
        setSeleccion(new Set());
      }
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [nodos, seleccionados, unico, portapapeles, dialogo, menu, visorId, abrir, pegar, avisar]);

  // ── Menú contextual ───────────────────────────────────────────────────
  function abrirMenu(evento: MouseEvent, nodo: Nodo | null) {
    evento.preventDefault();
    evento.stopPropagation();
    if (nodo && !seleccion.has(nodo.id)) setSeleccion(new Set([nodo.id]));
    setMenu({ x: evento.clientX, y: evento.clientY, nodo });
  }

  const opcionesMenu = (): OpcionMenu[] => {
    const objetivo = menu?.nodo;
    const puedeEscribirAqui = !!carpeta?.permisos.escribir;

    if (!objetivo) {
      return [
        {
          clave: 'nueva',
          etiqueta: 'Nueva carpeta',
          icono: <FolderPlus size={15} />,
          desactivada: !puedeEscribirAqui,
          motivo: 'No tienes permiso de escritura acá',
          accion: () => setDialogo('carpeta'),
        },
        {
          clave: 'subir',
          etiqueta: 'Subir archivos',
          icono: <Upload size={15} />,
          desactivada: !puedeEscribirAqui,
          motivo: 'No tienes permiso de escritura acá',
          accion: () => entradaArchivos.current?.click(),
        },
        {
          clave: 'subir-carpeta',
          etiqueta: 'Subir una carpeta',
          icono: <FolderUp size={15} />,
          desactivada: !puedeEscribirAqui,
          motivo: 'No tienes permiso de escritura acá',
          accion: () => entradaCarpeta.current?.click(),
        },
        {
          clave: 'pegar',
          etiqueta: 'Pegar',
          icono: <ClipboardPaste size={15} />,
          atajo: '⌘V',
          separadorAntes: true,
          desactivada: !portapapeles || !puedeEscribirAqui,
          accion: () => void pegar(),
        },
      ];
    }

    const varios = seleccionados.length > 1;
    return [
      {
        clave: 'abrir',
        etiqueta: objetivo.tipo === 'CARPETA' ? 'Abrir' : previsualizable(objetivo) ? 'Vista previa' : 'Descargar',
        icono: objetivo.tipo === 'CARPETA' ? <FolderPlus size={15} /> : <Eye size={15} />,
        accion: () => abrir(objetivo),
      },
      ...(objetivo.tipo === 'ARCHIVO'
        ? [
            {
              clave: 'descargar',
              etiqueta: 'Descargar',
              icono: <Download size={15} />,
              accion: () => seleccionados.forEach(descargar),
            },
            {
              clave: 'versiones',
              etiqueta: 'Historial de versiones',
              icono: <History size={15} />,
              desactivada: varios,
              accion: () => setDialogo('versiones'),
            },
          ]
        : []),
      {
        clave: 'renombrar',
        etiqueta: 'Renombrar',
        icono: <PenLine size={15} />,
        atajo: 'F2',
        separadorAntes: true,
        desactivada: varios || !objetivo.permisos.escribir,
        motivo: 'Necesitas permiso de escritura',
        accion: () => setDialogo('renombrar'),
      },
      {
        clave: 'copiar',
        etiqueta: 'Copiar',
        icono: <Copy size={15} />,
        atajo: '⌘C',
        accion: () => setPortapapeles({ ids: seleccionados.map((n) => n.id), modo: 'copiar' }),
      },
      {
        clave: 'cortar',
        etiqueta: 'Cortar',
        icono: <Scissors size={15} />,
        atajo: '⌘X',
        desactivada: !seleccionados.every((n) => n.permisos.eliminar),
        motivo: 'Mover exige permiso de eliminación en el origen',
        accion: () => setPortapapeles({ ids: seleccionados.map((n) => n.id), modo: 'cortar' }),
      },
      {
        clave: 'compartir',
        etiqueta: 'Compartir enlace…',
        icono: <Link2 size={15} />,
        separadorAntes: true,
        desactivada: varios || !objetivo.permisos.compartir,
        motivo: 'Necesitas permiso para compartir este elemento',
        accion: () => setDialogo('compartir'),
      },
      {
        clave: 'permisos',
        etiqueta: 'Permisos',
        icono: <ShieldCheck size={15} />,
        desactivada: varios || !objetivo.permisos.gestionar,
        motivo: 'Necesitas permiso de gestión',
        accion: () => setDialogo('permisos'),
      },
      {
        clave: 'eliminar',
        etiqueta: 'Enviar a la papelera',
        icono: <Trash2 size={15} />,
        atajo: '⌦',
        peligro: true,
        separadorAntes: true,
        desactivada: !seleccionados.every((n) => n.permisos.eliminar),
        motivo: 'Necesitas permiso de eliminación',
        accion: () => setDialogo('eliminar'),
      },
    ];
  };

  // ── Render ────────────────────────────────────────────────────────────
  const puedeEscribir = !!carpeta?.permisos.escribir;

  return (
    <div className="contenido">
      <div className="topbar">
        <div className="migas">
          {(listado?.ruta ?? []).map((miga, indice, todas) => (
            <span key={miga.id} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <button className={indice === todas.length - 1 ? 'actual' : ''} onClick={() => irA(miga.id)}>
                {miga.nombre}
              </button>
              {indice < todas.length - 1 && <span className="sep">/</span>}
            </span>
          ))}
          {buscando && <span className="muted" style={{ fontSize: 12.5 }}>· resultados de "{busqueda}"</span>}
        </div>

        <div className="buscador">
          <Search size={14} />
          <input
            placeholder="Buscar en esta carpeta…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            onKeyDown={(e) => e.key === 'Escape' && setBusqueda('')}
          />
        </div>
        {busqueda && (
          <button className="btn btn--fantasma btn--icono" onClick={() => setBusqueda('')} title="Limpiar búsqueda">
            <X size={15} />
          </button>
        )}
      </div>

      <div className="barra">
        <button
          className="btn btn--primario"
          disabled={!puedeEscribir}
          title={puedeEscribir ? 'Subir archivos' : 'No tienes permiso de escritura en esta carpeta'}
          onClick={() => entradaArchivos.current?.click()}
        >
          <Upload size={14} /> Subir
        </button>
        <button
          className="btn"
          disabled={!puedeEscribir}
          title={puedeEscribir ? 'Subir una carpeta completa, con su estructura' : 'No tienes permiso de escritura'}
          onClick={() => entradaCarpeta.current?.click()}
        >
          <FolderUp size={14} /> Subir carpeta
        </button>
        <button className="btn" disabled={!puedeEscribir} onClick={() => setDialogo('carpeta')}>
          <FolderPlus size={14} /> Nueva carpeta
        </button>

        <span className="separador" />

        <button
          className="btn btn--fantasma btn--icono"
          title="Descargar"
          disabled={seleccionados.length === 0 || seleccionados.some((n) => n.tipo === 'CARPETA')}
          onClick={() => seleccionados.forEach(descargar)}
        >
          <Download size={15} />
        </button>
        <button
          className="btn btn--fantasma btn--icono"
          title="Vista previa"
          disabled={!unico || unico.tipo !== 'ARCHIVO' || !previsualizable(unico)}
          onClick={() => unico && setVisorId(unico.id)}
        >
          <Eye size={15} />
        </button>
        <button
          className="btn btn--fantasma btn--icono"
          title="Renombrar (F2)"
          disabled={!unico?.permisos.escribir}
          onClick={() => setDialogo('renombrar')}
        >
          <PenLine size={15} />
        </button>
        <button
          className="btn btn--fantasma btn--icono"
          title="Copiar (⌘C)"
          disabled={seleccionados.length === 0}
          onClick={() => setPortapapeles({ ids: seleccionados.map((n) => n.id), modo: 'copiar' })}
        >
          <Copy size={15} />
        </button>
        <button
          className="btn btn--fantasma btn--icono"
          title="Pegar (⌘V)"
          disabled={!portapapeles || !puedeEscribir}
          onClick={() => void pegar()}
        >
          <ClipboardPaste size={15} />
        </button>
        <button
          className="btn btn--fantasma btn--icono"
          title="Compartir enlace"
          disabled={!unico?.permisos.compartir}
          onClick={() => setDialogo('compartir')}
        >
          <Link2 size={15} />
        </button>
        <button
          className="btn btn--fantasma btn--icono"
          title="Permisos"
          disabled={!unico?.permisos.gestionar}
          onClick={() => setDialogo('permisos')}
        >
          <ShieldCheck size={15} />
        </button>
        <button
          className="btn btn--fantasma btn--icono"
          title="Enviar a la papelera"
          disabled={seleccionados.length === 0 || !seleccionados.every((n) => n.permisos.eliminar)}
          onClick={() => setDialogo('eliminar')}
        >
          <Trash2 size={15} />
        </button>

        <span className="relleno" />

        {seleccionados.length > 0 && (
          <span className="muted" style={{ fontSize: 12 }}>
            {seleccionados.length} seleccionado(s)
          </span>
        )}
        <button
          className="btn btn--fantasma btn--icono"
          title={vista === 'lista' ? 'Ver en cuadrícula' : 'Ver en lista'}
          onClick={() => setVista(vista === 'lista' ? 'cuadricula' : 'lista')}
        >
          {vista === 'lista' ? <LayoutGrid size={15} /> : <List size={15} />}
        </button>
        <button
          className={`btn btn--fantasma btn--icono${verDetalles ? ' activo' : ''}`}
          title="Panel de detalles"
          onClick={() => setVerDetalles((v) => !v)}
        >
          <Info size={15} />
        </button>
      </div>

      <div className="explorador">
        {arbol && (
          <ArbolCarpetas
            arbol={arbol}
            carpetaActual={actual}
            ruta={(listado?.ruta ?? []).map((m) => m.id)}
            alAbrir={irA}
            alSoltar={(ids, destinoId) => void mover(ids, destinoId)}
          />
        )}

        <div
          className="panel"
          onClick={() => setSeleccion(new Set())}
          onDragEnter={(e) => {
            if (!e.dataTransfer.types.includes('Files')) return;
            contadorArrastre.current += 1;
            setArrastrandoArchivos(true);
          }}
          onDragOver={(e) => {
            if (e.dataTransfer.types.includes('Files')) e.preventDefault();
          }}
          onDragLeave={() => {
            contadorArrastre.current -= 1;
            if (contadorArrastre.current <= 0) setArrastrandoArchivos(false);
          }}
          onDrop={(e) => {
            contadorArrastre.current = 0;
            if (!e.dataTransfer.types.includes('Files')) return;
            e.preventDefault();
            setArrastrandoArchivos(false);
            // Se lee el arrastre antes de cualquier espera: la lista de items
            // del evento se vacía apenas termina el manejador.
            void entradasDeArrastre(e.dataTransfer)
              .then(subirArchivos)
              .catch(() => avisar('No se pudo leer lo que soltaste', 'error'));
          }}
        >
          {cuota?.efectiva && cuota.efectiva.estado !== 'OK' && (
            <div className={`aviso-cuota ${cuota.efectiva.estado.toLowerCase()}`}>
              <AlertTriangle size={15} />
              <span>
                {cuota.efectiva.estado === 'LLENA' ? (
                  <>
                    <strong>{cuota.efectiva.nombre}</strong> agotó su cuota de {tamanoTexto(cuota.efectiva.cuotaBytes)}.
                    No se pueden subir más archivos hasta liberar espacio.
                  </>
                ) : (
                  <>
                    <strong>{cuota.efectiva.nombre}</strong> va en el {cuota.efectiva.porcentaje}% de su cuota:
                    quedan {tamanoTexto(cuota.efectiva.libreBytes)}.
                  </>
                )}
              </span>
            </div>
          )}

          {isLoading && <div className="cargando">Cargando…</div>}
          {isError && (
            <div className="vacio">
              <p>{error instanceof Error ? error.message : 'No se pudo abrir la carpeta'}</p>
            </div>
          )}

          {!isLoading && !isError && nodos.length === 0 && (
            <div className="vacio">
              <div className="icono">
                <UploadCloud size={38} />
              </div>
              <p>{buscando ? 'Sin resultados para tu búsqueda.' : 'Esta carpeta está vacía.'}</p>
              {!buscando && puedeEscribir && (
                <p className="dim" style={{ fontSize: 12 }}>
                  Arrastra acá archivos o carpetas completas, o usa los botones de arriba.
                </p>
              )}
            </div>
          )}

          {!isLoading && nodos.length > 0 && (
            <ListaNodos
              nodos={nodos}
              vista={vista}
              seleccion={seleccion}
              cortados={new Set(portapapeles?.modo === 'cortar' ? portapapeles.ids : [])}
              orden={orden}
              alOrdenar={(campo: CampoOrden) =>
                setOrden((previo) => ({ campo, ascendente: previo.campo === campo ? !previo.ascendente : true }))
              }
              alClicear={alClicear}
              alAbrir={abrir}
              alMenu={abrirMenu}
              alSoltarEn={(ids, destinoId) => void mover(ids, destinoId)}
              alDescargar={descargar}
            />
          )}

          {arrastrandoArchivos && (
            <div className="soltar">
              <div className="soltar__caja">
                <UploadCloud size={34} />
                <div style={{ marginTop: 8 }}>
                  {puedeEscribir
                    ? `Suelta acá para subir a "${carpeta?.nombre}" — carpetas incluidas`
                    : 'No puedes escribir en esta carpeta'}
                </div>
              </div>
            </div>
          )}

          {subidas.length > 0 && (
            <div className="subidas">
              <div className="subidas__cabecera">
                <UploadCloud size={14} /> Subiendo {subidas.length} tarea(s)
              </div>
              {subidas.map((s) => (
                <div key={s.id} className="subidas__item">
                  <div className="nowrap">{s.nombre}</div>
                  {s.error ? (
                    <div style={{ color: 'var(--red)' }}>{s.error}</div>
                  ) : (
                    <div className="progreso">
                      <div style={{ width: `${s.porcentaje}%` }} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {verDetalles && (
          <PanelDetalles
            nodo={unico}
            cantidadSeleccion={seleccionados.length}
            alCerrar={() => setVerDetalles(false)}
            alVerPermisos={() => setDialogo('permisos')}
            alCompartir={() => setDialogo('compartir')}
            alVerVersiones={() => setDialogo('versiones')}
            alVer={() => unico && setVisorId(unico.id)}
            alDescargar={() => unico && descargar(unico)}
          />
        )}
      </div>

      <input
        ref={entradaArchivos}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files) subirArchivos([...e.target.files].map((archivo) => ({ archivo, ruta: '' })));
          e.target.value = '';
        }}
      />
      {/* `webkitdirectory` no está en los tipos de React, pero lo entienden
          todos los navegadores actuales: es lo que abre el selector de carpeta. */}
      <input
        ref={entradaCarpeta}
        type="file"
        hidden
        {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
        onChange={(e) => {
          if (e.target.files) subirArchivos(entradasDeSelector(e.target.files));
          e.target.value = '';
        }}
      />

      {enVisor && (
        <VisorDocumento
          nodo={enVisor}
          hermanos={nodos}
          alNavegar={(destino) => setVisorId(destino.id)}
          alDescargar={descargar}
          alCerrar={() => setVisorId(null)}
        />
      )}

      {menu && <MenuContextual x={menu.x} y={menu.y} opciones={opcionesMenu()} alCerrar={() => setMenu(null)} />}

      {dialogo === 'carpeta' && carpeta && (
        <DialogoNombre
          titulo="Nueva carpeta"
          etiqueta="Nombre de la carpeta"
          aceptar="Crear"
          modo="crear"
          alCerrar={() => setDialogo(null)}
          alAceptar={(nombre) => {
            acciones.crearCarpeta
              .mutateAsync({ padreId: carpeta.id, nombre })
              .then(() => avisar(`Carpeta "${nombre}" creada`))
              .catch(avisarError);
            setDialogo(null);
          }}
        />
      )}

      {dialogo === 'renombrar' && unico && (
        <DialogoNombre
          titulo="Renombrar"
          etiqueta="Nuevo nombre"
          valorInicial={unico.nombre}
          aceptar="Renombrar"
          modo="renombrar"
          alCerrar={() => setDialogo(null)}
          alAceptar={(nombre) => {
            acciones.renombrar
              .mutateAsync({ id: unico.id, nombre })
              .then(() => avisar('Renombrado'))
              .catch(avisarError);
            setDialogo(null);
          }}
        />
      )}

      {dialogo === 'permisos' && unico && <DialogoPermisos nodo={unico} alCerrar={() => setDialogo(null)} />}
      {dialogo === 'compartir' && unico && <DialogoCompartir nodo={unico} alCerrar={() => setDialogo(null)} />}
      {dialogo === 'versiones' && unico && <DialogoVersiones nodo={unico} alCerrar={() => setDialogo(null)} />}

      {dialogo === 'eliminar' && (
        <Confirmar
          titulo="Enviar a la papelera"
          peligro
          etiquetaConfirmar="Enviar a la papelera"
          mensaje={
            <>
              {seleccionados.length === 1 ? (
                <>
                  Se enviará <strong>{seleccionados[0]!.nombre}</strong> a la papelera.
                </>
              ) : (
                <>
                  Se enviarán <strong>{seleccionados.length} elementos</strong> a la papelera.
                </>
              )}
              {seleccionados.some((n) => n.tipo === 'CARPETA') && (
                <p className="muted" style={{ marginBottom: 0 }}>
                  Las carpetas se van con todo su contenido. Se puede restaurar desde la papelera.
                </p>
              )}
            </>
          }
          alConfirmar={() => void eliminar()}
          alCerrar={() => setDialogo(null)}
        />
      )}
    </div>
  );
}
